import { defineElement, FC, onDidLoad, useElements } from '@atomify/hooks';
import { GTMPush } from '@utilities/gtm';
import { isMobile, useMedia } from '@utilities/media-query';

interface Sub {
    trigger: HTMLElement;
    element: HTMLElement;
    inner: HTMLElement;
    height?: number;
    isOpen: boolean;
}

const SUB_DROPDOWN_ACTIVE_CLASS = 'navigation__item--dropdown-active';

const Navigation: FC = () => {
    const subTriggers = useElements<HTMLElement[]>('[js-hook-navigation-sub-trigger]');
    const links = useElements<HTMLElement[]>('[js-hook-navigation-link-item]');

    let subs: Sub[] = [];

    onDidLoad(() => {
        subs = getSubs();

        useMedia(({ tabletLandscape, tabletPortrait }) => {
            if (tabletLandscape || tabletPortrait) {
                setSubsHeight(subs);
                subs.forEach((sub) => closeSub(sub));
            }
        });

        bindSubMenuTriggerEvents();
        bindGTMEvents();
    });

    function bindSubMenuTriggerEvents() {
        subs.forEach((sub) => {
            sub.trigger.addEventListener('mouseenter', () => handleMouseenter(sub));
            sub.trigger.addEventListener('mouseleave', () => handleMouseleave(sub));
            sub.trigger.addEventListener('click', (e) => handleTriggerClick(e, sub));
        });
    }

    function handleMouseenter(sub: Sub) {
        if (!isMobile()) openSub(sub);
    }

    function handleMouseleave(sub: Sub) {
        if (!isMobile()) closeSub(sub);
    }

    function handleTriggerClick(_: Event, sub: Sub) {
        if (isMobile()) {
            closeOtherSubs(sub);
            sub.isOpen ? closeSub(sub) : openSub(sub);
        }
    }

    function openSub(sub: Sub) {
        if (sub.element) {
            sub.element.style.maxHeight = `${sub.height}px`;
            sub.isOpen = true;
        }
        sub.trigger.classList.add(SUB_DROPDOWN_ACTIVE_CLASS);
    }

    function closeSub(sub: Sub) {
        if (sub.element) {
            sub.element.style.maxHeight = `0px`;
            sub.isOpen = false;
        }
        sub.trigger.classList.remove(SUB_DROPDOWN_ACTIVE_CLASS);
    }

    function closeOtherSubs(sub: Sub) {
        subs.forEach((otherSub) => {
            if (otherSub !== sub) closeSub(otherSub);
        });
    }

    function getSubs() {
        const subs = [...subTriggers.current].map((trigger: HTMLElement) => {
            const sub: Sub = {
                trigger,
                element: trigger.querySelector('[js-hook-navigation-sub]') as HTMLElement,
                inner: trigger.querySelector('[js-hook-navigation-sub-inner]') as HTMLElement,
                isOpen: false,
            };

            return sub;
        });

        setSubsHeight(subs);

        return subs;
    }

    function setSubsHeight(subs: Sub[]) {
        subs.forEach((sub) => {
            const innerHeight = sub.inner.getBoundingClientRect().height;
            const innerStyles = window.getComputedStyle(sub.inner);
            const innerMargin =
                parseInt(innerStyles.marginTop) + parseInt(innerStyles.marginBottom);

            sub.height = innerHeight + innerMargin;
        });
    }

    function bindGTMEvents() {
        [...links.current].forEach((item) => {
            if (!item) return;
            const menuLink = item.querySelector<HTMLElement>('[js-hook-navigation-top-link]');

            menuLink?.addEventListener('click', () =>
                GTMPush({
                    event: 'menuUsed',
                    menuCategory: menuLink.innerText,
                    menuSubCategory: '',
                }),
            );
        });

        subs.forEach(({ trigger }) => {
            const subLinks = trigger.querySelectorAll<HTMLElement>('[js-hook-navigation-sub-link]');
            const menuTitle = trigger.querySelector<HTMLSpanElement>('span')?.innerText;

            subLinks.forEach((link) => {
                link.addEventListener('click', () =>
                    GTMPush({
                        event: 'menuUsed',
                        menuCategory: menuTitle,
                        menuSubCategory: link.innerText,
                    }),
                );
            });
        });
    }
};

defineElement('bpd-navigation', Navigation);
